import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { BillboardTable, BillboardHeading, BillboardDivider, BillboardFooter } from '../components/BillboardTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillboardView() {
  const [params] = useSearchParams();
  const [tournaments, setTournaments] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  // Today's date in Korea time (UTC+9). Recomputed continuously so the
  // billboard automatically rolls over to the next day at 00:00 KST.
  const computeTodayKst = () => {
    const now = new Date();
    const kst = new Date(now.getTime() + (9 * 60 - now.getTimezoneOffset()) * 60 * 1000);
    return `${kst.getFullYear()}-${String(kst.getMonth() + 1).padStart(2, '0')}-${String(kst.getDate()).padStart(2, '0')}`;
  };
  const urlDate = params.get('date');
  const [autoDate, setAutoDate] = useState(computeTodayKst());
  const date = urlDate || autoDate;

  // Poll the KST calendar every 30s. When the day flips, `autoDate` updates
  // and the fetchData effect below re-runs against the new date.
  useEffect(() => {
    if (urlDate) return; // explicit ?date= means the board is pinned by admin
    const tick = () => {
      const today = computeTodayKst();
      setAutoDate((prev) => (prev !== today ? today : prev));
    };
    tick();
    const id = setInterval(tick, 30 * 1000);
    return () => clearInterval(id);
  }, [urlDate]);
  const orientation = params.get('orientation') || '1920x1080';
  // Stable composite keys `event_number|time`. Empty => show all events for the date.
  const evsParam = params.get('evs');
  const selectedKeys = evsParam ? new Set(evsParam.split(',')) : null;
  const ctaText = params.get('cta') || '';
  const bgImg = params.get('bg') || '';

  const w = parseInt(orientation.split('x')[0]) || 1920;
  const h = parseInt(orientation.split('x')[1]) || 1080;
  const isLandscape = w > h;

  const fetchData = useCallback(async () => {
    try {
      const [tourRes, settRes] = await Promise.all([
        axios.get(`${API}/tournaments/live`, { params: { date }, timeout: 30000 }),
        axios.get(`${API}/settings`, { timeout: 20000 }),
      ]);
      const all = tourRes.data || [];
      const filtered = selectedKeys
        ? all.filter(t => selectedKeys.has(`${t.event_number}|${t.time}`))
        : all;
      setTournaments(filtered);
      setSettings(settRes.data);
    } catch (e) {
      console.warn('Fetch failed, will retry:', e.message);
      setTimeout(() => fetchData(), 10000);
    } finally {
      setLoading(false);
    }
    // selectedKeys is derived from the URL and fixed for the page's lifetime;
    // re-creating fetchData on it would restart the 60s poll needlessly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // ===== 24/7 KEEP-ALIVE DEFENSES =====

  // 1) Screen Wake Lock – prevents the device display from dimming/sleeping
  useEffect(() => {
    let wakeLock = null;
    const request = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen');
          wakeLock.addEventListener('release', () => { wakeLock = null; });
        }
      } catch (e) { /* user can deny or feature unsupported */ }
    };
    request();
    // Re-acquire if the tab becomes visible again (wake lock drops when hidden)
    const onVis = () => { if (document.visibilityState === 'visible') request(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (wakeLock) { try { wakeLock.release(); } catch { /* ignore */ } }
    };
  }, []);

  // 2) Immediate refresh the moment the tab regains focus (browsers throttle
  // setInterval when a tab is backgrounded, so data can go stale).
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible' && date) fetchData(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
  }, [date, fetchData]);

  // 3) Hourly hard-reload safety net — recovers from any stalled state
  // (memory leaks, stuck network, JS errors) when used on a signage monitor.
  useEffect(() => {
    const t = setTimeout(() => window.location.reload(), 60 * 60 * 1000);
    return () => clearTimeout(t);
  }, []);

  // 4) Aggressive server keep-alive — lightweight ping every 3 min to prevent
  // the deployed server from cold-sleeping on idle. Runs even when tab is
  // backgrounded (setInterval is throttled but still fires every ~1 min).
  useEffect(() => {
    const ping = () => {
      // Fire-and-forget; use fetch with no-cache to bypass any CDN caching.
      fetch(`${API}/`, { cache: 'no-store', keepalive: true }).catch(() => {});
    };
    ping();
    const pingInterval = setInterval(ping, 3 * 60 * 1000);
    return () => clearInterval(pingInterval);
  }, []);

  if (loading) return <div style={{ background: '#080404', width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontFamily: "'Oswald', sans-serif" }}>Loading...</div>;

  const bgUrl = bgImg || '';

  return (
    <div style={{
      width: w, height: h,
      background: '#080404',
      backgroundImage: bgUrl ? `url("${bgUrl}")` : 'none',
      backgroundSize: 'cover', backgroundPosition: 'center',
      fontFamily: "Arial, sans-serif",
      position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      margin: '0 auto',
      WebkitFontSmoothing: 'antialiased',
      MozOsxFontSmoothing: 'grayscale',
      textRendering: 'optimizeLegibility',
    }}>
      {/* Full-brightness background — no global dark overlay (table rows carry
          their own translucent backgrounds for readability). */}

      {/* HERO SPACER — plain; branding comes from the uploaded background */}
      <div style={{ height: isLandscape ? 300 : 400, flexShrink: 0, zIndex: 1 }} />

      <BillboardDivider />
      <BillboardHeading date={date} isLandscape={isLandscape} />

      <BillboardTable
        tournaments={tournaments}
        padding={isLandscape ? '0 40px 16px' : '0 24px 12px'}
        rowH={(n) => isLandscape
          ? Math.min(68, Math.max(42, (h - 500) / n - 2))
          : Math.min(90, Math.max(48, (h - 540) / n - 2))}
      />

      {/* CTA */}
      {ctaText && (
        <>
          <BillboardDivider />
          <div style={{ flexShrink: 0, padding: isLandscape ? '10px 40px' : '14px 24px', background: 'linear-gradient(135deg, #b91c1c 0%, #8b0000 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, zIndex: 1 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
            <span style={{ fontSize: isLandscape ? 14 : 16, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#fff' }}>{ctaText}</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
          </div>
        </>
      )}

      <BillboardFooter padding={isLandscape ? '8px 40px' : '6px 24px'} />
    </div>
  );
}
